// Chart ID Generation Utilities
// Hash-based unique identifiers for chart deduplication
// Architecture: v4.0.0 Multiple Charts Implementation

// Browser-compatible hash function (no Node.js crypto dependency)
import { devWarn, devError } from './devLog';
import { ChartIdComponents, ChartIdResult } from '../types/MultipleCharts';

/**
 * Simple hash function for browser compatibility
 * Alternative to crypto.createHash for browser environments
 */
function simpleHash(str: string): string {
  let hash = 0;
  if (str.length === 0) return hash.toString(16);
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Convert to positive hex string and pad to ensure consistent length
  const hexHash = Math.abs(hash).toString(16);
  return hexHash.padStart(16, '0');
}

/**
 * Generate unique chart ID based on location, datetime, and timezone
 * Uses simple hash for collision resistance and deduplication
 * 
 * Format: 'chart_' + first 16 characters of hash
 * Example: 'chart_a1b2c3d4e5f6g7h8'
 */
export function generateChartId(
  location: string,
  dateTime: string,
  timezone: string
): ChartIdResult {
  // Normalize input components
  const components: ChartIdComponents = {
    location: normalizeLocation(location),
    dateTime: normalizeDateTime(dateTime),
    timezone: normalizeTimezone(timezone)
  };
  
  // Create normalized string for hashing
  const normalizedString = `${components.location}|${components.dateTime}|${components.timezone}`;
  
  // Generate simple hash (browser-compatible)
  const hash = simpleHash(normalizedString);
  
  // Create chart ID from first 16 characters
  const chartId = `chart_${hash.substring(0, 16)}`;
  
  return {
    chartId,
    components,
    hash
  };
}

/**
 * Normalize location string for consistent hashing
 * Handles case sensitivity and common variations
 */
function normalizeLocation(location: string): string {
  return location
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')               // normalize whitespace
    .replace(/[^\w\s,-]/g, '')          // remove special characters except comma, dash
    .replace(/,\s*/g, ',')              // normalize comma spacing
    .replace(/-\s*/g, '-');             // normalize dash spacing
}

/**
 * Normalize datetime string for consistent hashing
 * Ensures ISO format and UTC normalization
 */
function normalizeDateTime(dateTime: string): string {
  try {
    // Parse and convert to ISO string (UTC)
    const date = new Date(dateTime);
    
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date: ${dateTime}`);
    }
    
    return date.toISOString();
  } catch (error) {
    devWarn('Failed to normalize datetime:', dateTime, error);
    // Return original if parsing fails
    return dateTime.trim();
  }
}

/**
 * Normalize timezone string for consistent hashing
 * Handles common timezone format variations
 */
function normalizeTimezone(timezone: string): string {
  return timezone
    .trim()
    .replace(/\s+/g, '')                // remove whitespace
    .toUpperCase();                     // normalize case
}

/**
 * Extract chart ID components from existing chart metadata
 * For generating IDs from existing chart data
 */
export function extractChartIdComponents(chartData: any): ChartIdComponents | null {
  try {
    let location = '';
    let dateTime = '';
    let timezone = 'UTC';

    // Try to extract from chartDateTime object first (new format from API)
    if (chartData.chartDateTime) {
      const cdt = chartData.chartDateTime;
      location = cdt.location || '';
      // Combine date and time into ISO-like format
      if (cdt.date && cdt.time) {
        dateTime = `${cdt.date}T${cdt.time}`;
      }
      timezone = cdt.timezone || 'UTC';
    }

    // Fallback to direct properties (legacy format)
    if (!location) {
      location = chartData.location || chartData.birthPlace || '';
    }
    if (!dateTime) {
      dateTime = chartData.dateTime || chartData.birthDateTime || '';
    }
    if (timezone === 'UTC') {
      timezone = chartData.timezone || chartData.timeZone || 'UTC';
    }

    // Try to get location from apiResponse if still empty
    if (!location && chartData.apiResponse?.basic_info) {
      location = chartData.apiResponse.basic_info.place ||
                 chartData.apiResponse.basic_info.location || '';
    }

    if (!location || !dateTime) {
      devWarn('Missing required components for chart ID generation:', {
        location,
        dateTime,
        timezone
      });
      return null;
    }

    return {
      location,
      dateTime,
      timezone
    };
  } catch (error) {
    devError('Failed to extract chart ID components:', error);
    return null;
  }
}

/**
 * Generate chart ID from existing chart data
 * Convenience function for existing charts
 */
export function generateChartIdFromData(chartData: any): ChartIdResult | null {
  const components = extractChartIdComponents(chartData);
  
  if (!components) {
    return null;
  }
  
  return generateChartId(
    components.location,
    components.dateTime,
    components.timezone
  );
}

/**
 * Validate chart ID format
 * Ensures ID follows expected format
 */
export function isValidChartId(chartId: string): boolean {
  const pattern = /^chart_[0-9a-f]{16}$/;
  return pattern.test(chartId);
}

/**
 * Compare two chart IDs for equality
 * Handles case sensitivity and format validation
 */
export function compareChartIds(id1: string, id2: string): boolean {
  if (!isValidChartId(id1) || !isValidChartId(id2)) {
    return false;
  }
  
  return id1.toLowerCase() === id2.toLowerCase();
}

/**
 * Generate display title for chart
 * Creates human-readable title from chart components
 */
export function generateChartTitle(components: ChartIdComponents, maxLength: number = 30): string {
  try {
    // Parse datetime for display
    const date = new Date(components.dateTime);
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Create title: Location - Date Time
    const title = `${components.location} - ${dateStr} ${timeStr}`;
    
    // Truncate if too long
    if (title.length > maxLength) {
      return title.substring(0, maxLength - 3) + '...';
    }
    
    return title;
  } catch (error) {
    devWarn('Failed to generate chart title:', error);
    // Fallback to location only
    const fallback = components.location;
    return fallback.length > maxLength 
      ? fallback.substring(0, maxLength - 3) + '...'
      : fallback;
  }
}

/**
 * Batch generate chart IDs for multiple charts
 * Efficient processing for bulk operations
 */
export function generateMultipleChartIds(charts: any[]): Map<string, ChartIdResult> {
  const results = new Map<string, ChartIdResult>();
  
  for (const [index, chartData] of charts.entries()) {
    const result = generateChartIdFromData(chartData);
    
    if (result) {
      results.set(result.chartId, result);
    } else {
      devWarn(`Failed to generate ID for chart at index ${index}:`, chartData);
    }
  }
  
  return results;
}